// Configuration for the VPM (video processing) backend integration.
//
// Deliberately separate from src/api/config.ts: the legacy API_BASE_URL bakes
// a /api/v1 suffix for the old Node backend, while the VPM services expose
// /v1/* on their own hosts. Nothing in the upload feature imports the legacy
// config, and no URL, bucket, or tenant value is ever hardcoded — everything
// arrives via REACT_APP_* vars inlined by CRA at build time.
//
// URLs may be absolute (https://...run.app) or relative (/vpm-upload) so a
// future Firebase Hosting rewrite to Cloud Run needs no code change.

// The browser's ONLY VPM base URL: the upload calls AND both status polls
// (GET /v1/uploads/{id}/status, GET /v1/videos/{id}/status) live on the public,
// CORS'd upload-api service (verified origin/main e58dd37; see CLAUDE.md
// "VPM API contract (browser-facing)").
export const UPLOAD_API_URL = process.env.REACT_APP_UPLOAD_API_URL || '';

// DEPRECATED for the browser: status-api is IAM-gated and browser-unreachable
// by design (ops/pr1-final-verification.md). Kept only for the rich ops-shape
// provider used in tests/tooling; no browser code path should depend on it.
export const STATUS_API_URL = process.env.REACT_APP_STATUS_API_URL || '';

// The tenant sent in the old X-Tenant header, used only when nobody is signed in
// (a signed-in user sends their Firebase ID token instead; see api/vpm/auth.ts, the
// only place that decides). upload-api accepts the header only in its `dual` mode.
export const VPM_TENANT = process.env.REACT_APP_VPM_TENANT || '';

// Adapter selection. Anything other than the literal 'live' means mock —
// the committed default. Going live is an env change, never a code change.
export const UPLOAD_MODE: 'mock' | 'live' =
  process.env.REACT_APP_UPLOAD_MODE === 'live' ? 'live' : 'mock';

// Upload FLOW selection (pre/post-retarget contract switch, Igor's PR-review
// flow). 'complete' (default): today's live-verified 2-call contract — the
// browser POSTs /v1/uploads/{id}/complete after the PUT and learns video_id
// synchronously. 'scan': the post-retarget flow — the browser MUST NOT call
// /complete (create-once idempotency would register the pre-scan landing URI
// that virus-scan deletes); it two-stage polls instead (scan status → video_id
// handoff → slim video status). Same anything-else-is-default idiom as
// UPLOAD_MODE: only the literal 'scan' flips it. Flip to 'scan' ONLY when
// Igor's bucket-retarget PR lands.
export const UPLOAD_FLOW: 'complete' | 'scan' =
  process.env.REACT_APP_UPLOAD_FLOW === 'scan' ? 'scan' : 'complete';

// Feature flag: when false the /upload and /my-uploads routes and the header
// nav entry are not registered at all, so the rest of the app is untouched.
export const UPLOAD_FEATURE_ENABLED = process.env.REACT_APP_ENABLE_UPLOAD === 'true';

// The "Add Content" mode chooser (incremental vs bulk). DEFAULT OFF: with bulk
// deferred to Igor's Storage Transfer Service workstream there is only one mode,
// so /upload routes straight into the incremental wizard. Set true to bring the
// chooser back (a one-line restore) once the bulk path is real; the AddContent +
// ModeChooser components are retained in the codebase for exactly that.
export const ADD_CONTENT_CHOOSER_ENABLED = process.env.REACT_APP_ENABLE_ADD_CONTENT_CHOOSER === 'true';

// Debug knob for exercising error UI against the mock adapter. Value is a
// comma-separated list of legs to fail, e.g. 'create', 'put', 'complete'.
export const UPLOAD_MOCK_FAILURES = (process.env.REACT_APP_UPLOAD_MOCK_FAILURES || '')
  .split(',')
  .map((leg) => leg.trim())
  .filter(Boolean);
